import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import {
  auth, db, doc, updateDoc, serverTimestamp,
} from '../../firebase/config';
import {
  updatePassword, EmailAuthProvider, reauthenticateWithCredential,
  sendPasswordResetEmail,
} from 'firebase/auth';
import SettingsLayout from './SettingsLayout';
import {
  Lock, Shield, Eye, EyeOff, Check, AlertCircle, KeyRound,
  LogOut, Mail, Loader2,
} from 'lucide-react';

const PrivacySecurityPage = () => {
  const navigate = useNavigate();
  const { user, userData } = useAuth();

  const [currentPw, setCurrentPw] = useState('');
  const [newPw, setNewPw] = useState('');
  const [confirmPw, setConfirmPw] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 2500);
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (!user) {
      showToast('You must be signed in', 'error');
      return;
    }
    if (!currentPw) {
      showToast('Enter your current password', 'error');
      return;
    }
    if (newPw.length < 8) {
      showToast('New password must be 8+ characters', 'error');
      return;
    }
    if (newPw !== confirmPw) {
      showToast('Passwords do not match', 'error');
      return;
    }
    setSaving(true);
    try {
      const credential = EmailAuthProvider.credential(user.email, currentPw);
      await reauthenticateWithCredential(user, credential);
      await updatePassword(user, newPw);
      try {
        await updateDoc(doc(db, 'users', user.uid), {
          passwordUpdatedAt: serverTimestamp(),
        });
      } catch (firestoreErr) {
        // Password changed successfully — this is just bookkeeping
        console.warn('passwordUpdatedAt write failed:', firestoreErr);
      }
      setCurrentPw('');
      setNewPw('');
      setConfirmPw('');
      showToast('Password updated');
    } catch (err) {
      console.error('Change password failed:', err);
      let msg = 'Could not update password';
      if (err.code === 'auth/wrong-password' || err.code === 'auth/invalid-credential') {
        msg = 'Current password is incorrect';
      } else if (err.code === 'auth/weak-password') {
        msg = 'New password is too weak';
      } else if (err.code === 'auth/requires-recent-login') {
        msg = 'Please sign out and back in, then try again';
      } else if (err.code === 'auth/too-many-requests') {
        msg = 'Too many attempts. Wait a few minutes and try again';
      }
      showToast(msg, 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleForgotPassword = async () => {
    if (!user?.email) {
      showToast('No email on your account', 'error');
      return;
    }
    try {
      await sendPasswordResetEmail(auth, user.email);
      showToast('Reset email sent to ' + user.email);
    } catch (err) {
      console.error('Reset email failed:', err);
      let msg = 'Could not send reset email';
      if (err.code === 'auth/too-many-requests') msg = 'Too many requests. Try again later';
      if (err.code === 'auth/user-not-found') msg = 'No account found';
      showToast(msg, 'error');
    }
  };

  return (
    <SettingsLayout
      title="Privacy & Security"
      subtitle="Manage your account security"
    >
      {/* Change password */}
      <div className="bg-white rounded-3xl border border-gray-100 p-5">
        <div className="flex items-center gap-2 mb-4">
          <Lock size={16} className="text-[#EFA9B6]" />
          <h2 className="font-serif font-bold text-gray-900 text-lg">Change password</h2>
        </div>

        <form onSubmit={handleChangePassword} className="space-y-3">
          <div>
            <label className="text-[10px] font-bold text-gray-500 uppercase tracking-wider block mb-1.5">
              Current password
            </label>
            <input
              type={showPw ? 'text' : 'password'}
              value={currentPw}
              onChange={(e) => setCurrentPw(e.target.value)}
              className="w-full bg-[#FCF8F9] border border-gray-100 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-[#EFA9B6]"
              placeholder="••••••••"
              autoComplete="current-password"
              required
            />
          </div>
          <div>
            <label className="text-[10px] font-bold text-gray-500 uppercase tracking-wider block mb-1.5">
              New password
            </label>
            <div className="relative">
              <input
                type={showPw ? 'text' : 'password'}
                value={newPw}
                onChange={(e) => setNewPw(e.target.value)}
                className="w-full bg-[#FCF8F9] border border-gray-100 rounded-xl px-4 py-3 pr-10 text-sm focus:outline-none focus:border-[#EFA9B6]"
                placeholder="Min 8 characters"
                autoComplete="new-password"
                required
              />
              <button
                type="button"
                onClick={() => setShowPw(!showPw)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </div>
          <div>
            <label className="text-[10px] font-bold text-gray-500 uppercase tracking-wider block mb-1.5">
              Confirm new password
            </label>
            <input
              type={showPw ? 'text' : 'password'}
              value={confirmPw}
              onChange={(e) => setConfirmPw(e.target.value)}
              className="w-full bg-[#FCF8F9] border border-gray-100 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-[#EFA9B6]"
              placeholder="••••••••"
              autoComplete="new-password"
              required
            />
          </div>

          <div className="flex justify-end pt-1">
            <button
              type="button"
              onClick={handleForgotPassword}
              className="text-xs font-bold text-[#EFA9B6] hover:underline"
            >
              Forgot current password?
            </button>
          </div>

          <button
            type="submit"
            disabled={saving || !currentPw || !newPw || !confirmPw}
            className="w-full py-3.5 rounded-full bg-[#010101] text-white font-bold text-sm hover:bg-black/90 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {saving ? (
              <>
                <Loader2 size={16} className="animate-spin" />
                Updating...
              </>
            ) : (
              <>
                <KeyRound size={16} />
                Update password
              </>
            )}
          </button>
        </form>
      </div>

      {/* Two-factor */}
      <div className="bg-white rounded-3xl border border-gray-100 p-5">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl bg-[#F4EEFF] flex items-center justify-center text-[#AF8BD4] shrink-0">
            <Shield size={18} />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-bold text-gray-900 text-sm">Two-factor authentication</h3>
            <p className="text-xs text-gray-500 mt-0.5 leading-relaxed">
              Extra security for your account. We'll email you a code whenever you sign in from a new device.
            </p>
            <span className="inline-block mt-2 px-2.5 py-0.5 bg-[#FFF5F6] text-[#EFA9B6] text-[10px] font-bold rounded-full">
              Coming soon
            </span>
          </div>
        </div>
      </div>

      {/* Sessions */}
      <div className="bg-white rounded-3xl border border-gray-100 p-5">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl bg-[#FCF8F9] flex items-center justify-center text-gray-500 shrink-0">
            <LogOut size={18} />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-bold text-gray-900 text-sm">Active sessions</h3>
            <p className="text-xs text-gray-500 mt-0.5">
              You're currently signed in on this device.
            </p>
            <p className="text-xs text-gray-400 mt-2">
              Signed in as <span className="font-bold text-gray-600">{user?.email}</span>
            </p>
          </div>
        </div>
      </div>

      {/* Info note */}
      <div className="flex items-start gap-2 p-4 bg-[#F4EEFF] rounded-2xl">
        <AlertCircle size={16} className="text-[#AF8BD4] shrink-0 mt-0.5" />
        <p className="text-xs text-gray-600 leading-relaxed">
          We never sell your personal information. Read our{' '}
          <button
            type="button"
            onClick={() => navigate('/dashboard/settings/privacy-policy')}
            className="font-bold underline"
          >
            Privacy Policy
          </button>{' '}
          to see how we handle your data.
        </p>
      </div>

      {toast && (
        <div className="fixed top-6 left-1/2 -translate-x-1/2 z-[70]">
          <div className={`px-5 py-3 rounded-full shadow-lg text-sm font-bold ${
            toast.type === 'error' ? 'bg-red-500 text-white' : 'bg-[#010101] text-white'
          }`}>
            {toast.msg}
          </div>
        </div>
      )}
    </SettingsLayout>
  );
};

export default PrivacySecurityPage;